import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Clock3, LogIn, MapPin, Shield, UserPlus, Users } from 'lucide-react';
import Header from '../../components/header/Header';
import Footer from '../../components/footer/Footer';
import LoginModal from '../../components/loginSection/LoginModal';
import { clubsService } from '../../services';
import { useUser } from '../../UserContext';
import './PublicClubInvitePage.css';

const formatDate = (value) => {
    if (!value) {
        return 'Recently sent';
    }

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
        return 'Recently sent';
    }

    return parsed.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
    });
};

const PublicClubInvitePage = () => {
    const { token } = useParams();
    const navigate = useNavigate();
    const { initialized, isAuthenticated, fetchUserProfile } = useUser();
    const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
    const [inviteData, setInviteData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isResolving, setIsResolving] = useState(false);
    const [isAccepting, setIsAccepting] = useState(false);
    const [error, setError] = useState('');
    const [accountMessage, setAccountMessage] = useState('');

    useEffect(() => {
        const loadInvite = async () => {
            setIsLoading(true);
            setError('');

            try {
                const data = await clubsService.getSharedClubInvitation(token);
                setInviteData(data);
            } catch (loadError) {
                setError(loadError.message || 'We could not load this venue invitation right now.');
                setInviteData(null);
            } finally {
                setIsLoading(false);
            }
        };

        if (token) {
            loadInvite();
        }
    }, [token]);

    useEffect(() => {
        const resolveInvite = async () => {
            if (!initialized || !isAuthenticated || !token) {
                return;
            }

            setIsResolving(true);
            setAccountMessage('');

            try {
                const resolution = await clubsService.resolveSharedClubInvitation(token);
                if (resolution?.isAlreadyMember && resolution?.dashboardUrl) {
                    navigate(resolution.dashboardUrl, { replace: true });
                    return;
                }

                if (!resolution?.canAccept) {
                    setAccountMessage('This invitation cannot be accepted with the account currently signed in.');
                }
            } catch (resolveError) {
                setAccountMessage(resolveError?.message || 'This invitation belongs to a different Shoot Connect account.');
            } finally {
                setIsResolving(false);
            }
        };

        resolveInvite();
    }, [initialized, isAuthenticated, navigate, token]);

    useEffect(() => {
        document.title = inviteData?.clubName
            ? `Join ${inviteData.clubName} | Shoot Connect`
            : 'Venue Invitation | Shoot Connect';
    }, [inviteData?.clubName]);

    const signUpPath = useMemo(() => `/register?token=${encodeURIComponent(token || '')}`, [token]);

    const handleAccept = async () => {
        setIsAccepting(true);
        setError('');

        try {
            const response = await clubsService.acceptSharedClubInvitation(token);
            await fetchUserProfile(true);
            if (response?.dashboardUrl) {
                navigate(response.dashboardUrl, { replace: true });
                return;
            }
            navigate('/dashboard/clubs', { replace: true });
        } catch (acceptError) {
            setError(acceptError.message || 'We could not accept this invitation right now.');
        } finally {
            setIsAccepting(false);
        }
    };

    return (
        <div className="public-club-invite-page">
            <Header
                openLoginModal={() => setIsLoginModalOpen(true)}
                isAuthenticated={initialized ? isAuthenticated : null}
                alwaysSolid
            />

            <main className="public-club-invite-main">
                <div className="container">
                    {isLoading && (
                        <div className="public-club-invite-state">
                            <h2>Loading venue invitation...</h2>
                        </div>
                    )}

                    {!isLoading && error && !inviteData && (
                        <div className="public-club-invite-state error">
                            <h2>We could not load this invitation</h2>
                            <p>{error}</p>
                        </div>
                    )}

                    {!isLoading && inviteData && (
                        <section className="public-club-invite-card">
                            <span className="public-club-invite-pill">Venue Invitation</span>
                            <h1>Join {inviteData.clubName}</h1>
                            <p className="public-club-invite-copy">
                                You have been invited to join this venue on Shoot Connect as <strong>{inviteData.roleLabel}</strong>.
                            </p>

                            <div className="public-club-invite-meta">
                                <span><MapPin size={16} /> {inviteData.clubAddress || 'Venue address coming soon'}</span>
                                <span><Users size={16} /> Role: {inviteData.roleLabel}</span>
                                <span><Clock3 size={16} /> Sent {formatDate(inviteData.createdAt)}</span>
                            </div>

                            <div className="public-club-invite-note">
                                <Shield size={16} />
                                This page keeps account details private until the invitation is accepted.
                            </div>

                            {inviteData.publicVenueUrl ? (
                                <div className="public-club-invite-links">
                                    <Link to={inviteData.publicVenueUrl}>View public venue page</Link>
                                </div>
                            ) : null}

                            {accountMessage && (
                                <div className="public-club-invite-inline error">
                                    {accountMessage}
                                </div>
                            )}

                            {error && inviteData && (
                                <div className="public-club-invite-inline error">
                                    {error}
                                </div>
                            )}

                            {isAuthenticated ? (
                                <div className="public-club-invite-actions">
                                    <button
                                        className="public-club-invite-primary"
                                        onClick={handleAccept}
                                        disabled={isAccepting || isResolving || !!accountMessage}
                                    >
                                        {isAccepting ? 'Accepting...' : 'Accept Invitation'}
                                    </button>
                                </div>
                            ) : (
                                <div className="public-club-invite-actions">
                                    <button className="public-club-invite-primary" onClick={() => setIsLoginModalOpen(true)}>
                                        <LogIn size={16} />
                                        Log In To Accept
                                    </button>
                                    <Link to={signUpPath} className="public-club-invite-secondary">
                                        <UserPlus size={16} />
                                        Sign Up
                                    </Link>
                                </div>
                            )}

                            {inviteData.requiresRegistration && !isAuthenticated && (
                                <p className="public-club-invite-footnote">
                                    New to Shoot Connect? Sign up with this invitation link and use the same email address the venue invited so your membership can be attached automatically.
                                </p>
                            )}
                        </section>
                    )}
                </div>
            </main>

            <Footer />

            {isLoginModalOpen && (
                <LoginModal onClose={() => setIsLoginModalOpen(false)} />
            )}
        </div>
    );
};

export default PublicClubInvitePage;
